/**
 * PATH: apps/hr/src/pages/employees/employee-detail/OverviewTab.tsx
 * PURPOSE: Profile overview tab with personal, employment, account, and additional info sections
 */

import { useMemo } from 'react'
import { CheckCircle2 } from 'lucide-react'
import { formatDate, formatCurrency, getInitials, formatStatus } from '../../../lib/utils'
import type { Employee } from '../../../types'
import { ProfileSection, ProfileRow, StatusBadge } from './detail-shared-ui'

interface OverviewTabProps {
  employee: Employee
  onEditEmployment: () => void
  onEditContact: () => void
  onEditEmergency: () => void
  onEditBank: () => void
  onEditPersonal: () => void
}

export default function OverviewTab({ employee, onEditEmployment, onEditContact, onEditEmergency, onEditBank, onEditPersonal }: OverviewTabProps) {
  const tenure = useMemo(() => {
    if (!employee.hireDate) return null
    const ms = new Date().getTime() - new Date(employee.hireDate).getTime()
    const years = Math.floor(ms / (365.25 * 24 * 60 * 60 * 1000))
    const months = Math.floor((ms % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000))
    return years > 0 ? `${years}y ${months}m` : `${months} months`
  }, [employee.hireDate])

  const formattedAddress = useMemo(() => {
    if (!employee.address) return null
    return [employee.address.city, employee.address.state, employee.address.country].filter(Boolean).join(', ') || null
  }, [employee.address])

  const fullAddress = useMemo(() => {
    if (!employee.address) return null
    const parts = [employee.address.line1, employee.address.line2, [employee.address.city, employee.address.state, employee.address.postalCode].filter(Boolean).join(' '), employee.address.country].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : null
  }, [employee.address])

  return (
    <div className="space-y-8">
      {/* Profile Header */}
      <div className="flex flex-col items-center py-8">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg ring-4 ring-white dark:ring-slate-800">
          {employee.avatarUrl ? <img src={employee.avatarUrl} alt={`${employee.firstName} ${employee.lastName}`} className="w-full h-full rounded-full object-cover" /> : getInitials(`${employee.firstName} ${employee.lastName}`)}
        </div>
        <div className="mt-4 flex items-center gap-2">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{employee.firstName} {employee.lastName}</h2>
          {employee.status === 'active' && <CheckCircle2 className="w-5 h-5 text-blue-500" />}
        </div>
        <p className="mt-1 text-slate-500 dark:text-slate-400">{employee.email || 'No email provided'}</p>
      </div>

      {/* Info Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Details */}
        <ProfileSection title="Personal details" onEdit={onEditPersonal}>
          <ProfileRow label="Full name" value={`${employee.firstName} ${employee.lastName}`} />
          <ProfileRow label="Date of Birth" value={employee.dateOfBirth ? formatDate(employee.dateOfBirth) : null} />
          <ProfileRow label="Gender" value={employee.gender ? employee.gender.charAt(0).toUpperCase() + employee.gender.slice(1) : null} />
          <ProfileRow label="Address" value={formattedAddress ? <span className="flex items-center gap-2">{employee.address?.country === 'Australia' && <span>🇦🇺</span>}{formattedAddress}</span> : null} />
          <ProfileRow label="Phone Number" value={employee.phone} />
          <ProfileRow label="Email" value={employee.email} />
        </ProfileSection>

        {/* Account Details */}
        <ProfileSection title="Account Details">
          <ProfileRow label="Employee ID" value={<code className="font-mono text-xs bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">{employee.employeeId}</code>} />
          <ProfileRow label="Account Created" value={employee.createdAt ? formatDate(employee.createdAt) : null} />
          <ProfileRow label="Last Updated" value={employee.updatedAt ? formatDate(employee.updatedAt) : null} />
          <ProfileRow label="Status" value={<StatusBadge status={employee.status === 'active' ? 'active' : 'inactive'} label={formatStatus(employee.status)} />} />
          <ProfileRow label="Account Verification" value={employee.status === 'active' ? <StatusBadge status="verified" /> : <StatusBadge status="pending" />} />
          <ProfileRow label="Login Access" value={employee.user_id ? <StatusBadge status="enabled" /> : <StatusBadge status="disabled" label="Not Enabled" />} />
        </ProfileSection>

        {/* Employment Information */}
        <ProfileSection title="Employment Information" onEdit={onEditEmployment}>
          <ProfileRow label="Position" value={employee.position} />
          <ProfileRow label="Department" value={employee.department} />
          <ProfileRow label="Employment Type" value={formatStatus(employee.employmentType)} />
          <ProfileRow label="Hire Date" value={employee.hireDate ? formatDate(employee.hireDate) : null} />
          <ProfileRow label="Tenure" value={tenure} />
          <ProfileRow label="Manager" value={employee.managerName} />
          <ProfileRow label="Location" value={employee.location} />
          {employee.payRate?.hourlyRate && employee.payRate.hourlyRate > 0 && (
            <ProfileRow label="Hourly Rate" value={<span className="text-teal-600 dark:text-teal-400 font-semibold">{formatCurrency(employee.payRate.hourlyRate)}/hr</span>} />
          )}
        </ProfileSection>

        {/* Additional Information */}
        <ProfileSection title="Additional Information">
          <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Emergency Contact</span>
              <button onClick={onEditEmergency} className="text-xs font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300">{employee.emergencyContact ? 'Edit' : 'Add'}</button>
            </div>
            {employee.emergencyContact ? (
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-200">{employee.emergencyContact.name}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{employee.emergencyContact.relationship} • {employee.emergencyContact.phone}</p>
              </div>
            ) : <p className="text-sm text-slate-400 dark:text-slate-500">No emergency contact on file</p>}
          </div>
          <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Bank Details</span>
              <button onClick={onEditBank} className="text-xs font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300">{employee.bankDetails ? 'Edit' : 'Add'}</button>
            </div>
            {employee.bankDetails ? (
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-200">{employee.bankDetails.accountName}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">BSB: {employee.bankDetails.bsb} • Acc: {employee.bankDetails.accountNumber}</p>
              </div>
            ) : <p className="text-sm text-slate-400 dark:text-slate-500">No bank details on file</p>}
          </div>
          <ProfileRow label="Full Address" value={fullAddress ? <span className="text-right max-w-[250px]">{fullAddress}</span> : null} />
          <ProfileRow label="Internal ID" value={<code className="font-mono text-[10px] bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">{employee.id}</code>} />
        </ProfileSection>
      </div>
    </div>
  )
}
